import { memo, ReactNode, useMemo, useState } from 'react'
import './index.scss'
import Icon from '../Icon'
import { useTranslation } from 'react-i18next'
import { COLOR } from '../../data/variables'
import Button from '../Button'
import { ICON } from '../Icon/data'
import { useFormat } from '../../hooks/useFormat'
import { DateTime } from 'luxon'

export type Infobox = {
  status?: 'info' | 'success' | 'warning' | 'error'
  type?: 'inline' | 'toast'
  label?: string
  message?: string | ReactNode
  timestamp?: string
  closable?: boolean
  onClose?: () => void
  time?: number
  timeAdded?: string
}

const Infobox = memo(({
  status = 'info', type = 'inline', label, message, timestamp, closable = true, onClose
}: Infobox) => {
  const { t } = useTranslation()
  const { stringToHtml } = useFormat()

  const [isVisible, setIsVisible] = useState<boolean>(true)
  
  const iconMap = useMemo(() => {
    switch (status) {
      case 'success':
        return <Icon icon={ICON.CHECKMARK_FILLED} color={COLOR.SUCCESS_GREEN} />
        break
      
      case 'warning':
        return <Icon icon={ICON.WARNING} color={COLOR.WARNING_YELLOW} fill={COLOR.BLACK} />
        break
      
      case 'error':
        return <Icon icon={ICON.ERROR_FILLED} color={COLOR.ERROR_RED} />
        break

      default:
        return <Icon icon={ICON.INFORMATION_FILLED} color={COLOR.INFO_BLUE} />
        break
    }
  }, [status])

  if (!isVisible) return null

  return (
    <div className={`Infobox ${status} ${type}`}>
      <div className="Infobox__border" />
      <div className="Infobox__icon">{iconMap}</div>
      <div className="Infobox__content">
        <div className="Infobox__content-label">{label}</div>
        {typeof message === 'string' ? stringToHtml(message, "Infobox__content-message") : message}
        {timestamp && (
          <div className="Infobox__content-timestamp">
            {`${t('Timestamp')} ${DateTime.fromISO(timestamp).toFormat('HH:mm:ss')}`}
          </div>
        )}
      </div>
      {closable && (
        <div className="Infobox__close">
          <Button
            type='ghost'
            size='s'
            onClick={() => {
              setIsVisible(false)
              if (typeof onClose !== 'undefined') onClose()
            }}
            label={<Icon icon={ICON.CLOSE} />}
          />
        </div>
      )}
    </div>
  )
})

export default Infobox
